import React, { useContext, useRef, useCallback } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { LoadableAfterPaint } from '@confluence/loadable';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	NativeCollabPresenceStateContainer,
	useNativeCollabPresenceState,
} from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import {
	createSingleQueryParamHook,
	useRouteName,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
	VIEW_PAGE,
} from '@confluence/named-routes/entry-points/routes.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';

import {
	type PresenceParticipant,
	type PresenceSessionData,
	PresenceStoreContainer,
} from '../presence-store/index.ts';
import type {
	CanvasCollaborators,
	PresenceContentMode,
	PresencePageContentType,
} from '../presenceTypes.ts';
import type { NestedAgentPresenceChild } from '../presence-avatar-group/index.ts';
import { CURRENT_USER_DUMMY_SESSION_ID } from '../presenceUtils.ts';
import { useAnonymousAsset } from '../page-presence/useAnonymousAsset.ts';
import { useViewPresencePreloadPending } from '../preloadViewPresenceBootstrap.ts';

import type {
	ContentHeaderPresenceUserQuery as ContentHeaderPresenceUserQuery$data,
	ContentHeaderPresenceUserQueryVariables as ContentHeaderPresenceUserQuery$variables,
	ContentHeaderPresenceUserQuery_user_AtlassianUser,
} from './__types__/ContentHeaderPresenceUserQuery.ts';
import type {
	ContentHeaderPresenceContentSettingsQuery$data,
	ContentHeaderPresenceContentSettingsQuery$variables,
} from './__generated__/ContentHeaderPresenceContentSettingsQuery.graphql.ts';

const PagePresence = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PagePresence" */ '../page-presence/PagePresence.tsx'
			)
		).PagePresence,
});

const CanvasPresence = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CanvasPresence" */ './CanvasPresence.tsx'))
			.CanvasPresence,
});

// PersonalizationContract fragment baseline. Colocated here so the content-view presence setting is
// owned by its consumer, then spread by whichever operation supplies it. Spread unmasked below
// because this operation is still read as `$data` through `useQueryInterop`.
// See confluence/openspec/changes/personalization-contract.
export const ContentHeaderPresenceContentSettingFragment: GraphQLTaggedNode = graphql`
	fragment ContentHeaderPresence_contentSetting on ConfluenceTeamPresence {
		isEnabledOnContentView
	}
`;

const ContentHeaderPresenceContentSettingsQuery = graphql`
	query ContentHeaderPresenceContentSettingsQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_teamPresenceContentSetting(cloudId: $cloudId, spaceKey: $spaceKey) {
			...ContentHeaderPresence_contentSetting @relay(mask: false)
		}
	}
`;

export type ContentHeaderPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType?: string | null;
	isViewMode?: boolean;
	analyticSource?: string;
	children?: NestedAgentPresenceChild;
};

const createDefaultParticipant = ({
	presenceId,
	activity,
	user,
	isGuest,
}: {
	presenceId: string;
	activity: PresenceActivity;
	user?: ContentHeaderPresenceUserQuery_user_AtlassianUser | null;
	isGuest: boolean;
}): PresenceParticipant | undefined => {
	if (!user) {
		return undefined;
	}

	const sessions: PresenceSessionData = {
		[CURRENT_USER_DUMMY_SESSION_ID]: {
			activity,
		},
	};

	const usablePresenceId = user?.id ?? presenceId;

	return {
		id: usablePresenceId,
		userId: user?.id ?? 'unidentified',
		presenceId: usablePresenceId,
		name: user?.displayName ?? '',
		src: user?.photos?.[0]?.value ?? '',
		isCurrentUser: true,
		isGuest,
		sessions,
	};
};

const UFO_SEGMENT_NAME = 'content-header-presence';
const useEmbeddedParam = createSingleQueryParamHook('embedded');
const useParentProductParam = createSingleQueryParamHook('parentProduct');

export const ContentHeaderPresenceComponent = ({
	collaborators,
	contentId,
	contentType,
	isViewMode,
	analyticSource,
	children,
}: ContentHeaderPresenceProps): React.JSX.Element | null => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { cloudId } = useSessionData();
	const isGuest = useIsGuest();
	const spaceKey = useSpaceKey();

	const isEditPage = useIsEditorPage();
	const isOrdinaryViewRoute = useRouteName({
		selector: (name: string | undefined) =>
			name === VIEW_PAGE.name || name === VIEW_BLOG.name || name === VIEW_BLOG_DATE_LEGACY.name,
	});
	const embedded = useEmbeddedParam();
	const parentProduct = useParentProductParam();

	const isLivePage = useIsCurrentPageLive();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const { fetchAnonymousAsset } = useAnonymousAsset();

	const isCanvasType =
		contentType === 'whiteboard' || contentType === 'database' || contentType === 'slide';
	const { data: userData, error: userQueryError } = useQueryInterop<
		ContentHeaderPresenceUserQuery$data,
		ContentHeaderPresenceUserQuery$variables
	>(graphql`
		query ContentHeaderPresenceUserQuery {
			user: confluence_atlassianUser(current: true) {
				id
				displayName
				photos {
					value
					isPrimary
				}
			}
		}
	`);

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const presenceIdRef = useRef<string>(presenceId);

	let contentMode: PresenceContentMode = 'view';

	if (isLivePage) {
		contentMode = 'live';
	} else if (isEditPage) {
		contentMode = 'edit';
	}

	const activity: PresenceActivity =
		contentMode === 'edit' || (contentMode === 'live' && (isLiveEditMode || !isViewMode))
			? 'editor'
			: 'viewer';

	const useBootstrapSetting =
		isOrdinaryViewRoute &&
		!embedded &&
		!parentProduct &&
		!isLivePage &&
		!isEditPage &&
		!isCanvasType &&
		expVal('confluence_exp_view_presence_contract', 'isEnabled', false);
	const isPresencePreloadPending = useViewPresencePreloadPending({
		cloudId,
		contentId,
		spaceKey: spaceKey ?? '',
	});
	const { data: settingData, error: settingsQueryError } = useQueryInterop<
		ContentHeaderPresenceContentSettingsQuery$data,
		ContentHeaderPresenceContentSettingsQuery$variables
	>(ContentHeaderPresenceContentSettingsQuery, {
		variables: {
			cloudId,
			spaceKey: spaceKey ?? '',
		},
		// Do not race the route-owned bootstrap (or its legacy fallback) on client navigation.
		skip:
			isCanvasType || contentMode !== 'view' || (useBootstrapSetting && isPresencePreloadPending),
		// Reuse the setting written by bootstrap; recover with the legacy query if hydration misses it.
		...(useBootstrapSetting ? { fetchPolicy: 'cache-first' as const } : {}),
	});

	const error = userQueryError || settingsQueryError;

	const isSettingEnabledOnView = useCallback(() => {
		return (
			isCanvasType ||
			settingData?.confluence_teamPresenceContentSetting?.isEnabledOnContentView === true
		);
	}, [isCanvasType, settingData]);

	const handleError = (err: Error) => {
		experienceTracker.fail({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			error: err,
		});

		markErrorAsHandled(err);

		return null;
	};

	if (!contentType) {
		return null;
	}

	if (error) {
		handleError(error);
	}

	if (contentMode === 'view' && !isSettingEnabledOnView()) {
		experienceTracker.abort({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			reason: `Real-time visibility is disabled on view mode`,
		});

		return null;
	}

	return (
		<PresenceStoreContainer
			participant={createDefaultParticipant({
				presenceId: presenceIdRef.current,
				activity,
				user: userData?.user,
				isGuest,
			})}
			fetchAnonymousAsset={fetchAnonymousAsset}
			onError={handleError}
		>
			{isCanvasType ? (
				<CanvasPresence
					collaborators={collaborators}
					contentId={contentId}
					contentType={contentType}
					analyticSource={analyticSource}
				>
					{children}
				</CanvasPresence>
			) : (
				<PagePresence
					contentId={contentId}
					contentMode={contentMode}
					contentType={contentType as PresencePageContentType}
					activity={activity}
					useBootstrapToken={useBootstrapSetting}
					spaceKey={spaceKey ?? ''}
					analyticSource={analyticSource}
				>
					{children}
				</PagePresence>
			)}
		</PresenceStoreContainer>
	);
};

export const ContentHeaderPresence = (props: ContentHeaderPresenceProps): React.JSX.Element => {
	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			<UFOSegment name={UFO_SEGMENT_NAME}>
				<NativeCollabPresenceStateContainer scope={props.contentId}>
					<ExperienceStart name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
					<ContentHeaderPresenceComponent {...props} />
				</NativeCollabPresenceStateContainer>
			</UFOSegment>
		</ErrorBoundary>
	);
};
